import React from "react";
import styled from "styled-components";
import MainContainer from "../../components/MainContainer";
import { useTheme } from "../../contexts/ThemeContext";

const TermsPage = () => {
  const { theme } = useTheme();

  return (
    <MainContainer>
      <TermsContainer $theme={theme}>
        <Header>
          <Title>TERMS OF USE</Title>
          <LastUpdated>Last updated: 14 July 2025</LastUpdated>
        </Header>

        <Content>
          <Introduction $theme={theme}>
            Welcome to Playbox.vip (the "Site", "Service", "we", "us" or "our").
            These Terms of Use (the "Terms") form a legally binding contract
            between you ("you" or the "User") and us. By accessing, browsing or
            using the Service, you confirm that you have read, understood and
            agree to be bound by these Terms and by all applicable laws and
            regulations. If you do not agree, you must not use the Service.
          </Introduction>

          <Section>
            <SectionTitle $theme={theme}>1. Service Description</SectionTitle>
            <SectionContent>
              The Service enables Users to upload still images of people and,
              via proprietary artificial-intelligence ("AI") models, generate
              short video clips based on those images (the "AI‑Generated
              Content").
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle $theme={theme}>2. Eligibility</SectionTitle>
            <SectionContent>
              <BulletPoint>
                You must be at least 18 years old (or the age of majority in
                your jurisdiction, whichever is higher) and have the legal
                capacity to enter into these Terms.
              </BulletPoint>
              <BulletPoint>
                You affirm that you are the person depicted in every image you
                upload; you must not upload images of any other individual for
                AI processing.
              </BulletPoint>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle $theme={theme}>
              3. User Accounts and Security
            </SectionTitle>
            <SectionContent>
              <BulletPoint>
                You may need to create an account. You are responsible for
                maintaining the confidentiality of your login credentials and
                for all activity under your account.
              </BulletPoint>
              <BulletPoint>
                You must promptly notify us of any unauthorized use of your
                account.
              </BulletPoint>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle $theme={theme}>4. User Content</SectionTitle>
            <SectionContent>
              "User Content" means all images, data, text, audio, video, and
              other material that you upload, transmit or otherwise make
              available through the Service.
            </SectionContent>

            <SubSection>
              <SubSectionTitle $theme={theme}>
                4.1 Your Representations
              </SubSectionTitle>
              <SectionContent>
                By uploading User Content you represent and warrant that:
                <BulletList>
                  <BulletPoint>
                    you are the sole subject depicted in the image(s) and you
                    own or otherwise control all rights necessary to submit the
                    User Content;
                  </BulletPoint>
                  <BulletPoint>
                    the User Content does not infringe, misappropriate or
                    violate any intellectual-property, privacy, publicity or
                    other proprietary rights; and
                  </BulletPoint>
                  <BulletPoint>
                    the User Content complies with Section 4.3 (Prohibited
                    Content).
                  </BulletPoint>
                </BulletList>
              </SectionContent>
            </SubSection>

            <SubSection>
              <SubSectionTitle $theme={theme}>
                4.2 Licence to Us
              </SubSectionTitle>
              <SectionContent>
                You grant us a limited, non-exclusive, royalty-free licence to
                host, store, reproduce, modify, adapt, create derivative works
                (including AI‑Generated Content) and otherwise use the User
                Content solely for the purpose of operating and providing the
                Service to you, as well as for improving our models, security,
                compliance and support.
                <ImportantNote $theme={theme}>
                  <strong>Important:</strong> We never share your User Content
                  (uploaded images) or AI‑Generated Content with any third
                  parties. Your content remains private and is only processed by
                  our systems to provide the Service.
                </ImportantNote>
              </SectionContent>
            </SubSection>

            <SubSection>
              <SubSectionTitle $theme={theme}>
                4.3 Prohibited Content
              </SubSectionTitle>
              <SectionContent>
                You agree that you will not upload, generate or distribute via
                the Service any content that:
                <BulletList>
                  <BulletPoint>
                    depicts minors (anyone under 18) in any sexual context;
                  </BulletPoint>
                  <BulletPoint>
                    depicts any person other than yourself without that person's
                    explicit, verifiable consent;
                  </BulletPoint>
                  <BulletPoint>
                    promotes violence, terrorism, self-harm or hate;
                  </BulletPoint>
                  <BulletPoint>
                    is unlawful, defamatory, fraudulent or otherwise
                    objectionable;
                  </BulletPoint>
                  <BulletPoint>
                    infringes any third-party rights; or
                  </BulletPoint>
                  <BulletPoint>
                    violates any applicable law or regulation.
                  </BulletPoint>
                </BulletList>
                NSFW or sexually explicit content featuring yourself, created
                from your own uploaded images, is permitted provided it complies
                with the above restrictions and all applicable laws. You remain
                fully responsible for such content, including any sharing or
                publication.
              </SectionContent>
            </SubSection>
          </Section>

          <Section>
            <SectionTitle $theme={theme}>5. AI‑Generated Content</SectionTitle>
            <SectionContent>
              <BulletPoint>
                AI‑Generated Content is created algorithmically and may contain
                artifacts, inaccuracies or distortions. We do not guarantee that
                AI‑Generated Content will be accurate, realistic, or fit for any
                particular purpose.
              </BulletPoint>
              <BulletPoint>
                You acknowledge that AI‑Generated Content may unintentionally
                produce sensitive or offensive material; you assume all risk
                arising from its use or publication.
              </BulletPoint>
              <BulletPoint>
                Subject to your compliance with these Terms and the payment of
                any applicable fees, we assign to you all of our right, title
                and interest in and to the AI‑Generated Content we specifically
                create for you, excluding any underlying software or models.
              </BulletPoint>
              <BulletPoint>
                <strong>Your Responsibility.</strong> You are solely responsible
                for all AI‑Generated Content created from your uploads,
                including any sharing, publication or other use. You must ensure
                that such content complies with all applicable laws, regulations
                and these Terms.
              </BulletPoint>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle $theme={theme}>
              6. Intellectual‑Property Rights
            </SectionTitle>
            <SectionContent>
              <BulletPoint>
                All intellectual-property rights in the Site, underlying models,
                software, trademarks and logos are and shall remain our
                exclusive property or that of our licensors.
              </BulletPoint>
              <BulletPoint>
                Nothing in these Terms grants you any right to use our
                trademarks or service marks without prior written consent.
              </BulletPoint>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle $theme={theme}>7. Fees and Payment</SectionTitle>
            <SectionContent>
              If the Service offers paid tiers, credits or subscriptions, you
              agree to pay all fees in accordance with the pricing and billing
              terms posted on the Site. Fees are non-refundable except as
              required by law or expressly stated otherwise.
            </SectionContent>

            <SubSection>
              <SubSectionTitle $theme={theme}>
                7.1 Refunds Policy
              </SubSectionTitle>
              <SectionContent>
                We do not provide refunds under any circumstances, including but
                not limited to:
                <BulletList>
                  <BulletPoint>
                    Purchased credits, regardless of whether they have been used
                    or not;
                  </BulletPoint>
                  <BulletPoint>
                    Subscription fees or any other payments made to the Service;
                  </BulletPoint>
                  <BulletPoint>
                    Credits consumed for AI-Generated Content, regardless of
                    your satisfaction with the results, technical issues, or any
                    other reason.
                  </BulletPoint>
                </BulletList>
                Once credits are used to generate content, they are considered
                consumed and will not be restored or refunded. You acknowledge
                that AI-Generated Content may vary in quality and that the
                consumption of credits is final upon generation.
              </SectionContent>
            </SubSection>
          </Section>

          <Section>
            <SectionTitle $theme={theme}>
              8. DMCA / Copyright Complaints
            </SectionTitle>
            <SectionContent>
              <BulletPoint>
                We respect intellectual-property rights and respond to notices
                of alleged infringement in accordance with the Digital
                Millennium Copyright Act ("DMCA").
              </BulletPoint>
              <BulletPoint>
                For takedown requests please email{" "}
                <EmailLink $theme={theme} href="mailto:support@playbox.vip">
                  support@playbox.vip
                </EmailLink>{" "}
                with the required information.
              </BulletPoint>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle $theme={theme}>9. Disclaimers</SectionTitle>
            <SectionContent>
              THE SERVICE AND ALL CONTENT ARE PROVIDED "AS IS" AND "AS
              AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED,
              INCLUDING BUT NOT LIMITED TO MERCHANTABILITY, FITNESS FOR A
              PARTICULAR PURPOSE AND NON-INFRINGEMENT.
              <br />
              WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE
              OR ERROR-FREE.
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle $theme={theme}>
              10. Limitation of Liability
            </SectionTitle>
            <SectionContent>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR
              ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL OR EXEMPLARY
              DAMAGES (INCLUDING LOSS OF PROFITS, DATA, GOODWILL OR OTHER
              INTANGIBLE LOSSES) ARISING OUT OF OR IN CONNECTION WITH THE
              SERVICE.
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle $theme={theme}>11. Indemnification</SectionTitle>
            <SectionContent>
              You agree to indemnify, defend and hold harmless the Site and its
              affiliates, officers, directors, employees and agents from any
              claims, damages, liabilities, costs and expenses (including
              reasonable attorneys' fees) arising from:
              <BulletList>
                <BulletPoint>(a) your User Content;</BulletPoint>
                <BulletPoint>(b) your use of the Service;</BulletPoint>
                <BulletPoint>(c) your breach of these Terms; or</BulletPoint>
                <BulletPoint>
                  (d) your violation of any law or third-party rights.
                </BulletPoint>
              </BulletList>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle $theme={theme}>12. Termination</SectionTitle>
            <SectionContent>
              <BulletPoint>
                We may suspend or terminate your access to the Service at any
                time, with or without notice, for conduct that we believe
                violates these Terms or is harmful to other Users, us or third
                parties.
              </BulletPoint>
              <BulletPoint>
                Sections 4–13 shall survive any termination.
              </BulletPoint>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle $theme={theme}>13. Modifications</SectionTitle>
            <SectionContent>
              <BulletPoint>
                We may revise these Terms from time to time. Material changes
                will be posted on this page and, where appropriate, notified to
                you by email.
              </BulletPoint>
              <BulletPoint>
                By continuing to use the Service after changes take effect, you
                accept the revised Terms.
              </BulletPoint>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle $theme={theme}>
              14. Governing Law and Dispute Resolution
            </SectionTitle>
            <SectionContent>
              These Terms are governed by the laws of Singapore, without regard
              to its conflict-of-laws principles. Any dispute shall be resolved
              through good-faith negotiations; if unresolved, the courts of
              Singapore shall have exclusive jurisdiction.
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle $theme={theme}>15. Contact</SectionTitle>
            <SectionContent>
              Questions about these Terms may be directed to{" "}
              <EmailLink $theme={theme} href="mailto:support@playbox.vip">
                support@playbox.vip
              </EmailLink>
              .
            </SectionContent>
          </Section>

          <Footer $theme={theme}>
            © 2025 Playbox.vip All rights reserved.
            <br />
            Terms of Service of Playbox - NSFW AI Video Generator
          </Footer>
        </Content>
      </TermsContainer>
    </MainContainer>
  );
};

export default TermsPage;

const TermsContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;
  color: ${({ $theme }) => ($theme === "light" ? "#1a1a2e" : "#ffffff")};
  font-family: DM Sans;
  font-weight: 400;
  line-height: 1.6;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-family: DM Sans;
  font-weight: 600;
  margin-bottom: 10px;
  background: linear-gradient(135deg, #fff, #fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const LastUpdated = styled.p`
  font-size: 14px;
  color: #888;
  margin: 0;
`;

const Content = styled.div``;

const Introduction = styled.p`
  font-size: 16px;
  margin-bottom: 30px;
  padding: 20px;
  background: ${({ $theme }) =>
    $theme === "light"
      ? "rgba(128, 128, 128, 0.05)"
      : "rgba(145, 145, 145, 0.1)"};
  border-left: 4px solid #6eadff;
  border-radius: 8px;
  color: ${({ $theme }) => ($theme === "light" ? "#1a1a2e" : "#ffffff")};
`;

const Section = styled.div`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-family: DM Sans;
  font-weight: 600;
  color: #fff;
  margin-bottom: 15px;
  border-bottom: ${({ $theme }) =>
    $theme === "light" ? "1px solid #e0e0e0" : "1px solid #333"};
  padding-bottom: 8px;
`;

const SubSection = styled.div`
  margin: 20px 0;
  padding-left: 20px;
`;

const SubSectionTitle = styled.h3`
  font-size: 16px;
  font-family: DM Sans;
  font-weight: 500;
  color: #fff;
  margin-bottom: 10px;
`;

const SectionContent = styled.div`
  font-size: 14px;
  margin-bottom: 15px;
`;

const BulletList = styled.div`
  margin: 10px 0;
`;

const BulletPoint = styled.div`
  margin: 8px 0;
  padding-left: 20px;
  position: relative;

  &:before {
    content: "•";
    color: #6eadff;
    font-weight: bold;
    position: absolute;
    left: 0;
  }
`;

const ImportantNote = styled.div`
  margin: 15px 0;
  padding: 15px;
  background: ${({ $theme }) =>
    $theme === "light" ? "rgba(83, 83, 83, 0.05)" : "rgba(55, 55, 55, 0.1)"};
  border-left: 4px solid #6eadff;
  border-radius: 6px;
  font-size: 14px;
  color: ${({ $theme }) => ($theme === "light" ? "#1a1a2e" : "#ffffff")};
  font-family: DM Sans;
`;

const EmailLink = styled.a`
  color: #fff;
  text-decoration: none;

  &:hover {
    color: #fff;
    text-decoration: underline;
  }
`;

const Footer = styled.div`
  margin-top: 50px;
  padding-top: 30px;
  border-top: ${({ $theme }) =>
    $theme === "light" ? "1px solid #e0e0e0" : "1px solid #333"};
  text-align: center;
  font-size: 12px;
  color: ${({ $theme }) =>
    $theme === "light" ? "rgba(26, 26, 46, 0.6)" : "#888"};
`;
